// ---------------------------------------------------------------------------
// Ajuda de sessão — SEM deslogar ninguém.
//
// Regra do app: só o botão "Sair" (ou "Excluir conta", ou o fim do modo equipe
// numa conta de equipe) encerra uma sessão. Falha de rede, timeout ou erro de
// token NUNCA levam para /entrar nem chamam signOut.
//
// Aqui ficam só dois utilitários:
//  · reconhecer erro de token vencido, para não mostrar erro feio na tela;
//  · renovar a sessão (getSession) e repetir a consulta UMA vez, para aguentar
//    o sinal fraco no meio de um evento.
// ---------------------------------------------------------------------------
import { supabase } from './supabase';

function extrairTexto(erro: unknown): string {
  if (!erro) return '';
  if (typeof erro === 'string') return erro;
  if (erro instanceof Error) return `${erro.name} ${erro.message}`;
  if (typeof erro === 'object') {
    const o = erro as { message?: string; error_description?: string; details?: string; hint?: string };
    return `${o.message ?? ''} ${o.error_description ?? ''} ${o.details ?? ''} ${o.hint ?? ''}`;
  }
  return '';
}

// Marcas de token vencido / sessão inválida (não de rede).
function indicaSessaoInvalida(texto: string): boolean {
  const t = texto.toLowerCase();
  return (
    t.includes('refresh token') ||
    t.includes('jwt expired') ||
    t.includes('invalid_grant') ||
    t.includes('invalid token') ||
    t.includes('pgrst301') ||
    t.includes('not authenticated')
  );
}

// Reconhece um erro já capturado (objeto ou texto) como sessão inválida/vencida.
export function ehErroDeSessaoInvalida(erro: unknown): boolean {
  return indicaSessaoInvalida(extrairTexto(erro));
}

// ---------------------------------------------------------------------------
// Token de atualização que NÃO EXISTE MAIS no servidor.
//
// Diferente de "sinal fraco": aqui a sessão guardada neste navegador é lixo
// (token antigo, trocado de projeto, revogado). Ela nunca vai conseguir renovar
// e, se ficar no armazenamento, o app fica repetindo o mesmo erro a cada tela.
// Esse é o único caso em que apagamos a sessão guardada — e mesmo assim sem
// mandar ninguém para a tela de login.
// ---------------------------------------------------------------------------
export function ehRefreshTokenInvalido(erro: unknown): boolean {
  const t = extrairTexto(erro).toLowerCase();
  return (
    t.includes('refresh token not found') ||
    t.includes('invalid refresh token') ||
    t.includes('refresh_token_not_found') ||
    t.includes('refresh token already used') ||
    t.includes('invalid_grant')
  );
}

// Apaga do armazenamento do navegador qualquer sessão antiga do Supabase.
function limparSessaoGuardada(): void {
  try {
    const chaves: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const chave = localStorage.key(i);
      if (chave && /^sb-.*-auth-token/.test(chave)) chaves.push(chave);
    }
    chaves.forEach((c) => localStorage.removeItem(c));
  } catch {
    /* sem acesso ao armazenamento: segue o jogo */
  }
}

// Descarta a sessão antiga: tira do navegador e avisa o Supabase para soltar o
// estado em memória (scope "local" não faz chamada de rede e não afeta outros
// aparelhos). Depois disso o app simplesmente fica sem sessão, sem erro na tela.
let descartando = false;

export async function descartarSessaoAntiga(): Promise<void> {
  if (descartando) return;
  descartando = true;
  limparSessaoGuardada();
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    /* já limpamos o armazenamento; nada mais a fazer */
  } finally {
    setTimeout(() => {
      descartando = false;
    }, 1500);
  }
}

// Erro que indica token vencido / não autenticado (401/PGRST301/jwt).
export function ehErroDeAuth(erro: unknown): boolean {
  if (!erro) return false;
  if (typeof erro === 'object') {
    const status = (erro as { status?: number }).status;
    const code = String((erro as { code?: string }).code ?? '').toLowerCase();
    if (status === 401 || code === 'pgrst301') return true;
  }
  return indicaSessaoInvalida(extrairTexto(erro));
}

// Roda a consulta. Se falhar por sessão, pede ao Supabase uma sessão nova
// (getSession renova o token quando precisa) e tenta UMA vez mais. Se continuar
// falhando, deixa o erro subir — quem chamou mostra "Tente de novo", sem
// nunca navegar para /entrar.
export async function comSessao<T>(fazer: () => Promise<T>): Promise<T> {
  try {
    return await fazer();
  } catch (e) {
    if (!ehErroDeAuth(e)) throw e;
    try {
      await supabase.auth.getSession();
    } catch {
      /* segue para a segunda tentativa de qualquer forma */
    }
    return await fazer();
  }
}

// ---------------------------------------------------------------------------
// Interceptador único de fetch (usado só pelo cliente Supabase).
//
// Se uma chamada volta 401 por token vencido, pede ao Supabase uma sessão nova
// (getSession renova o token) e repete a MESMA chamada UMA vez. Nunca desloga e
// nunca manda para /entrar. Se continuar falhando, a própria tela mostra
// "Tente de novo". Chamadas de login/renovação (/auth/v1) ficam de fora para
// não cair em laço.
// ---------------------------------------------------------------------------
let renovandoAgora = false;

export const fetchComRetentativa: typeof fetch = async (input, init) => {
  const resposta = await fetch(input, init);
  const url =
    typeof input === 'string' ? input : input instanceof URL ? input.href : input.url ?? '';
  const ehRotaDeAuth = url.includes('/auth/v1/');
  if (resposta.status === 401 && !renovandoAgora && !ehRotaDeAuth) {
    renovandoAgora = true;
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const h = new Headers(init?.headers);
      if (session) h.set('Authorization', 'Bearer ' + session.access_token);
      return await fetch(input, { ...init, headers: h });
    } catch (erro) {
      // O getSession pode falhar porque a sessão guardada não existe mais no
      // servidor. Nesse caso, limpamos o resto velho e devolvemos a resposta.
      if (ehRefreshTokenInvalido(erro)) void descartarSessaoAntiga();
      return resposta;
    } finally {
      renovandoAgora = false;
    }
  }
  return resposta;
};